import { Injectable, inject } from '@angular/core';
import { DropEvent } from '@interactjs/actions/drop/DropEvent';
import interact from 'interactjs';
import { DragActiveEntryCSSClass } from '../../../../../core/constants-repository/sortable-rows.constants';
import { ShiveRenderService } from '../../../../../core/services/rendering/shive-render.service';
import {
  TodoContainerDateAttribute,
  TodoEntryVariableIntervalWrapperCSSClass,
  TodoEntryWrapperCSSClass,
  TodoPlannerDropzoneCSSClass,
} from '../../../utils/todo-planner.constants';
import { TodoEntryDragService } from './todo-entry-drag.service';
import { TodoEntrySharedDragAndDropService } from './todo-entry-shared-drag-and-drop.service';

@Injectable({
  providedIn: 'root',
})
export class TodoContainerDropService {
  // DI
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly todoEntryDragService = inject(TodoEntryDragService);
  private readonly todoEntrySharedDAndDService = inject(TodoEntrySharedDragAndDropService);

  /**
   * Init drop zones for the containers (e.g. the lanes in the week view)
   */
  public initContainerDropzones(): void {
    interact(`.${TodoPlannerDropzoneCSSClass}`).dropzone({
      accept: `.${TodoEntryWrapperCSSClass}.${DragActiveEntryCSSClass}`,
      overlap: 0.5,
      ondragenter: (event: DropEvent) => {
        // Check if drop element is a real dropzone and if so return new dropzone.
        const dropElement = event.target as HTMLElement;
        const newContainer = this.checkAndUpdateEnteredDropzone(dropElement);

        if (this.todoEntrySharedDAndDService.switchedLane()) {
          const draggableElement = event.relatedTarget as HTMLElement;
          this.todoEntrySharedDAndDService.handleLaneSwitch(
            draggableElement,
            dropElement,
            newContainer,
          );

          this.todoEntryDragService.firstTouchAfterLaneSwitch();
        }
      },
      ondragleave: (event: DropEvent) => {
        this.containerLeft(event);
      },
    });
  }

  private containerLeft(event: DropEvent): void {
    const dropzoneElement = event.target as HTMLElement;

    if (!this.isCorrectDropzone(dropzoneElement)) {
      return;
    }

    const [container] = this.getCurrentTodoContainer(dropzoneElement);
    this.todoEntrySharedDAndDService.setContainer(container, true);
  }

  private isCorrectDropzone(dropzoneElement: HTMLElement): boolean {
    return dropzoneElement.classList.contains(TodoPlannerDropzoneCSSClass);
  }

  private getCurrentTodoContainer(dropzoneElement: HTMLElement): [HTMLElement, string] | null {
    const currentTodoEntryContainer = dropzoneElement.querySelector(
      `.${TodoEntryVariableIntervalWrapperCSSClass}`,
    ) as HTMLElement | null;

    if (!currentTodoEntryContainer) {
      return null;
    }

    const laneDate = this.shiveRenderService.getAttribute(
      currentTodoEntryContainer,
      TodoContainerDateAttribute,
    );

    return [currentTodoEntryContainer, laneDate];
  }

  private checkAndUpdateEnteredDropzone(dropzoneElement: HTMLElement): HTMLElement | null {
    if (!this.isCorrectDropzone(dropzoneElement)) {
      return null;
    }

    const [currentTodoEntryContainer, laneDate] = this.getCurrentTodoContainer(dropzoneElement);

    this.todoEntrySharedDAndDService.setLaneDate(laneDate);
    this.todoEntrySharedDAndDService.setContainer(currentTodoEntryContainer);
    return currentTodoEntryContainer;
  }
}
